<template>
  <div class="tutorial-page">
    <common-header />

    <div class="tutorial-container">
      <div class="tutorial-wrapper">
        <div class="tutorial-header">
          <div class="tutorial-title text-center padding-30 text-bold text-32">
            {{ info.title || "邮箱授权码教程" }}
          </div>
        </div>
        <div
          class="tutorial-content bg-white radius-20 padding-30 margin-top-20"
          v-loading="loading"
        >
          <!-- 富文本内容 -->
          <div
            v-if="info?.content"
            class="content-text"
            v-html="processedContent"
          ></div>

          <!-- 额外的图片展示（如果 images 数组中有图片且不在 content 中） -->
          <div
            v-else-if="!loading"
            class="text-center padding-40 text-999-color"
          >
            暂无内容
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CommonHeader from "@/components/CommonHeader.vue";
import { getText } from "@/api/user";

export default {
  name: "EmailAuthTutorial",
  components: {
    CommonHeader,
  },
  data() {
    return {
      info: {},
      loading: false,
    };
  },
  computed: {
    // 处理后的内容，确保图片 URL 正确
    processedContent() {
      if (!this.info?.content) {
        return "";
      }
      let content = this.info.content;

      // 确保图片 URL 完整（如果图片 URL 不完整，可能需要处理）
      // 这里可以根据实际情况调整图片 URL 的处理逻辑
      content = content.replace(
        /<img\s+([^>]*?)src=["']([^"']+)["']([^>]*?)>/gi,
        (match, before, src, after) => {
          // 如果图片 URL 是相对路径，可能需要转换为绝对路径
          // 这里假设图片 URL 已经是完整的
          return `<img ${before}src="${src}"${after} style="max-width: 100%; height: auto; display: block; margin: 20px auto; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);">`;
        }
      );

      return content;
    },
    // 获取额外的图片（在 images 数组中但不在 content 中的图片）
    extraImages() {
      if (!this.info?.images || !Array.isArray(this.info.images)) {
        return [];
      }

      // 从 content 中提取所有图片 URL
      const contentImages = [];
      if (this.info.content) {
        const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
        let match;
        while ((match = imgRegex.exec(this.info.content)) !== null) {
          contentImages.push(match[1]);
        }
      }

      // 返回不在 content 中的图片
      return this.info.images.filter((img) => !contentImages.includes(img));
    },
  },
  mounted() {
    this.fetchTutorialContent();
  },
  methods: {
    // 获取授权教程内容
    fetchTutorialContent() {
      this.loading = true;
      // type=2 表示邮箱授权教程
      getText("2")
        .then((response) => {
          this.loading = false;
          if (response.data && response.data.length > 0) {
            this.info = response.data[0];
          } else {
            this.info = {};
            this.$message.warning("暂无教程内容");
          }
        })
        .catch((error) => {
          this.loading = false;
          this.$message.error("获取内容失败，请稍后重试");
          console.error("获取教程内容失败:", error);
        });
    },
    // 图片加载错误处理
    handleImageError(event) {
      event.target.style.display = "none";
      console.error("图片加载失败:", event.target.src);
    },
    // 图片加载成功处理
    handleImageLoad(event) {
      // 图片加载成功后的处理（如果需要）
    },
  },
};
</script>

<style scoped lang="scss">
.tutorial-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #fff6f8 0%, #f3f7ff 100%);
  padding-bottom: 40px;
}

.tutorial-container {
  max-width: 1200px;
  margin: 20px auto;
  padding: 0 20px;
}

.tutorial-wrapper {
  width: 100%;
}

.tutorial-header {
  width: 100%;
}

.tutorial-title {
  color: #212121;
}

.tutorial-content {
  box-shadow: 0 12px 30px rgba(31, 46, 134, 0.08);
  min-height: 400px;
}

.content-text {
  line-height: 1.8;
  color: #333;
  word-wrap: break-word;
  font-size: 14px;

  // 段落样式
  p {
    margin-bottom: 16px;
    color: #333;
    line-height: 1.8;
  }

  // 标题样式
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 24px;
    margin-bottom: 16px;
    color: #212121;
    font-weight: 600;
    line-height: 1.5;
  }

  h1 {
    font-size: 24px;
  }

  h2 {
    font-size: 20px;
  }

  h3 {
    font-size: 18px;
  }

  h4 {
    font-size: 16px;
  }

  // 列表样式
  ul,
  ol {
    margin: 16px 0;
    padding-left: 30px;

    li {
      margin-bottom: 8px;
      line-height: 1.8;
    }
  }

  // 链接样式
  a {
    color: #d85a70;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #c54a60;
      text-decoration: underline;
    }
  }

  // 强调文本
  strong,
  b {
    font-weight: 600;
    color: #212121;
  }

  em,
  i {
    font-style: italic;
  }

  // 代码样式
  code {
    background: #f5f7fa;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: "Courier New", monospace;
    font-size: 13px;
    color: #e83e8c;
  }

  pre {
    background: #f5f7fa;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
    line-height: 1.6;

    code {
      background: transparent;
      padding: 0;
      color: #333;
    }
  }

  // 引用样式
  blockquote {
    border-left: 4px solid #d85a70;
    padding-left: 16px;
    margin: 16px 0;
    color: #666;
    font-style: italic;
  }

  // 表格样式
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;

    th,
    td {
      border: 1px solid #ebeef5;
      padding: 12px;
      text-align: left;
    }

    th {
      background: #f7f7f9;
      font-weight: 600;
      color: #212121;
    }

    tr:nth-child(even) {
      background: #fafafa;
    }
  }

  // 分割线
  hr {
    border: none;
    border-top: 1px solid #ebeef5;
    margin: 24px 0;
  }
}

// 富文本中的图片样式
::v-deep .content-text {
  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 20px auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s;

    &:hover {
      transform: scale(1.02);
    }
  }

  // 图片居中
  p {
    img {
      display: block;
      margin: 20px auto;
    }
  }

  // 图片说明
  img + em,
  img + span {
    display: block;
    text-align: center;
    color: #999;
    font-size: 12px;
    margin-top: -10px;
    margin-bottom: 20px;
  }
}

// 额外图片展示区域
.extra-images {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid #ebeef5;

  .image-item {
    margin-bottom: 30px;
    text-align: center;

    &:last-child {
      margin-bottom: 0;
    }

    img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 0 auto;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transition: transform 0.3s;

      &:hover {
        transform: scale(1.02);
      }
    }
  }
}
</style>

